import React from 'react';
import PropTypes from 'prop-types';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import ImageIcon from '@mui/icons-material/Image';
import { imageShape } from '../shapes/ImageShapes';
import Delete from '@mui/icons-material/Delete';
import NumberField from './UI/NumberField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';

class ImageForm extends React.Component {
  state = {
    x: null,
    y: null,
    width: null,
    height: null,
    keepAspectRatio: false,
    aspectRatio: null,
    playCount: null,
    endBehavior: null,
    inputIsValid: {
      x: true,
      y: true,
      width: true,
      height: true,
    },
  };

  componentDidUpdate(prevProps) {
    if (prevProps.image.endBehavior !== this.props.image.endBehavior) {
      this.setState({ endBehavior: null });
    }
    if (prevProps.image.playCount !== this.props.image.playCount) {
      this.setState(prev => ({ playCount: null,
        inputIsValid: { ...prev.inputIsValid, playCount: true } }));
    }
    const previous = prevProps.image.frames[0];
    const current = this.props.image.frames[0];
    const sizeChanged = previous.width !== current.width || previous.height !== current.height;
    const positionChanged = prevProps.image.x !== this.props.image.x || prevProps.image.y !== this.props.image.y;
    if (sizeChanged || positionChanged) {
      this.setState(prevState => ({
        ...(sizeChanged ? {
          width: null,
          height: null,
          // Keep the captured ratio after applying our own rounded dimensions;
          // adopt the actual ratio for external changes such as undo/redo.
          aspectRatio: prevState.keepAspectRatio
            ? (prevState.width === current.width && prevState.height === current.height
              ? prevState.aspectRatio : current.width / current.height)
            : null,
        } : {}),
        ...(positionChanged ? { x: null, y: null } : {}),
        inputIsValid: {
          ...prevState.inputIsValid,
          ...(sizeChanged ? { width: true, height: true } : {}),
          ...(positionChanged ? { x: true, y: true } : {}),
        },
      }));
    }
  }

  handleInputChange = (value, field, isValid) => {
    this.setState(prevState => {
      const next = {
        [field]: value,
        inputIsValid: { ...prevState.inputIsValid, [field]: isValid },
      };
      if (prevState.keepAspectRatio && isValid && (field === 'width' || field === 'height')) {
        const otherField = field === 'width' ? 'height' : 'width';
        // Hold the ratio steady while typing; round only the derived dimension.
        next[otherField] = Math.max(1, Math.round(
          field === 'width' ? value / prevState.aspectRatio : value * prevState.aspectRatio
        ));
        next.inputIsValid[otherField] = true;
      }
      return next;
    });
  };

  handleAspectRatioChange = event => {
    const keepAspectRatio = event.target.checked;
    this.setState({
      keepAspectRatio,
      aspectRatio: keepAspectRatio ? this.getWidth() / this.getHeight() : null,
    });
  };

  handleSubmit = () => {
    if (!this.canSubmit()) {
      return;
    }
    this.props.onSubmit(this.props.image.name, {
      x: this.getX(),
      y: this.getY(),
      width: this.getWidth(),
      height: this.getHeight(),
      ...(this.props.image.frames.length > 1 ? {
        playCount: this.state.playCount ?? this.props.image.playCount ?? 0,
        endBehavior: this.state.endBehavior ?? this.props.image.endBehavior ?? 'hold',
      } : {}),
    });
  };

  getX = () => {
    return this.state.x !== null ? this.state.x : this.props.image.x;
  };

  getY = () => {
    return this.state.y !== null ? this.state.y : this.props.image.y;
  };

  getWidth = () => {
    return this.state.width !== null ? this.state.width : this.props.image.frames[0].width;
  };

  getHeight = () => {
    return this.state.height !== null ? this.state.height : this.props.image.frames[0].height;
  };

  canSubmit = () => Object.values(this.state.inputIsValid).every(Boolean);

  render() {
    return (
      <div className="glint-animate-entry glint-hover-lift" style={this.props.style}>
        <List dense disablePadding>
          <ListItem divider>
            <ListItemIcon>
              <ImageIcon />
            </ListItemIcon>
            <ListItemText primary={this.props.image.name} />
            <ListItemSecondaryAction>
              <IconButton
                className="glint-tactile"
                edge="end"
                aria-label="delete"
                onClick={() => this.props.onDeleteClick(this.props.image.name)}
              >
                <Delete />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        </List>
        <Box m={1}>
          {this.props.image.frames.length > 1 && <Box mb={2}>
            <NumberField
              label="Total plays (0 = forever)"
              value={this.state.playCount ?? this.props.image.playCount ?? 0}
              onChange={(value, valid) => this.handleInputChange(value, 'playCount', valid)}
              additionalValidation={value => Number.isSafeInteger(value) && value <= 65536}
              allowPositive allowZero allowDecimals fullWidth
            />
            <Typography variant="body2" color="text.secondary">
              1 plays once. Larger values set the total number of plays. Apply to update the timeline.
            </Typography>
            <TextField select fullWidth label="After final play" sx={{ mt: 2 }}
              value={this.state.endBehavior ?? this.props.image.endBehavior ?? 'hold'}
              onChange={event => this.setState({ endBehavior: event.target.value })}>
              <MenuItem value="hold">Hold last frame</MenuItem>
              <MenuItem value="hide">Hide layer</MenuItem>
            </TextField>
          </Box>}
          <FormControlLabel
            control={
              <Checkbox
                checked={this.state.keepAspectRatio}
                onChange={this.handleAspectRatioChange}
                disabled={!this.state.keepAspectRatio &&
                  (!this.state.inputIsValid.width || !this.state.inputIsValid.height)}
              />
            }
            label="Keep aspect ratio"
          />
          <Grid sx={{ width: '100%' }}
            container
            spacing={1}
            direction="row"
            justifyContent="space-around"
            alignItems="flex-start"
          >
            <Grid size={6}>
              <NumberField fullWidth
                label="Width"
                onChange={(width, isValid) => this.handleInputChange(width, 'width', isValid)}
                onEnterPress={this.handleSubmit}
                value={this.getWidth()}
                error={!this.state.inputIsValid.width}
                allowPositive
              />
            </Grid>
            <Grid size={6}>
              <NumberField fullWidth
                label="Height"
                onChange={(height, isValid) => this.handleInputChange(height, 'height', isValid)}
                onEnterPress={this.handleSubmit}
                value={this.getHeight()}
                error={!this.state.inputIsValid.height}
                allowPositive
              />
            </Grid>
            <Grid size={6}>
              <NumberField fullWidth
                label="X"
                onChange={(x, isValid) => this.handleInputChange(x, 'x', isValid)}
                onEnterPress={this.handleSubmit}
                value={this.getX()}
                error={!this.state.inputIsValid.x}
                allowPositive
                allowZero
                allowNegative
              />
            </Grid>
            <Grid size={6}>
              <NumberField fullWidth
                label="Y"
                onChange={(y, isValid) => this.handleInputChange(y, 'y', isValid)}
                onEnterPress={this.handleSubmit}
                value={this.getY()}
                error={!this.state.inputIsValid.y}
                allowPositive
                allowNegative
                allowZero
              />
            </Grid>
          </Grid>
          <Box mt={1}>
            <Button
              className="glint-tactile"
              variant="contained"
              fullWidth
              sx={{ minHeight: 44 }}
              disabled={!this.canSubmit()}
              onClick={this.handleSubmit}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </div>
    );
  }
}

ImageForm.propTypes = {
  image: imageShape.isRequired,
  onSubmit: PropTypes.func,
  onDeleteClick: PropTypes.func,
  style: PropTypes.object,
};
ImageForm.defaultProps = {
  onSubmit() {},
  onDeleteClick() {},
  style: {},
};

export default ImageForm;
